import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import DeanListPhoto from "../assets/recent-deans-list.webp";
import ToastmastersPhoto from "../assets/recent-toastmasters.webp";
import PortraitPhoto from "../assets/recent-building.webp";

type RecentUpdate = {
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  imagePosition?: string;
  alt: string;
};

const recentUpdates: RecentUpdate[] = [
  {
    eyebrow: "Building / 2026",
    title: "Meridian is taking shape.",
    description:
      "A logistics platform spread across a Next.js frontend, a .NET gateway and a small fleet of microservices. The latest work has been live tracking, service communication and making the whole thing behave like one product.",
    image: PortraitPhoto,
    imagePosition: "center 34%",
    alt: "Dulain standing in front of a stone building",
  },
  {
    eyebrow: "Academics / The running tally",
    title: "Dean's List, four times over.",
    description:
      "The certificates are nice. The more useful part has been learning how to stay curious and consistent while the coursework gets harder and the side projects get less sensible.",
    image: DeanListPhoto,
    imagePosition: "center 42%",
    alt: "Dulain receiving a Dean's List certificate at SLIIT",
  },
  {
    eyebrow: "Toastmasters / Leadership",
    title: "A year behind the gavel.",
    description:
      "Serving as president of Central Link Toastmasters has been a practical lesson in speaking clearly, leading people and keeping a room moving—even when the plan changes halfway through.",
    image: ToastmastersPhoto,
    imagePosition: "center 44%",
    alt: "Dulain spending time with fellow students",
  },
];

export default function RecentUpdatesSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const reduceMotion = useReducedMotion();
  const activeUpdate = recentUpdates[activeIndex] ?? recentUpdates[0]!;

  useEffect(() => {
    if (isPaused || reduceMotion) return;

    const interval = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % recentUpdates.length);
    }, 5500);

    return () => window.clearInterval(interval);
  }, [isPaused, reduceMotion]);

  return (
    <section
      aria-labelledby="recent-updates-heading"
      className="relative border-t border-white/10 bg-[#0c0c0c] px-4 py-20 md:px-6 md:py-24"
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex items-end justify-between gap-6">
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.35em] text-white/50">03 — Recent Updates</p>
            <h2 id="recent-updates-heading" className="font-display text-3xl text-white md:text-5xl">
              Lately, in no particular order.
            </h2>
          </div>
          <span className="hidden font-mono text-xs text-white/35 md:block">UPDATED AS THINGS HAPPEN</span>
        </div>

        <div
          className="grid min-h-[32rem] overflow-hidden border border-white/10 bg-[#101010] md:min-h-0 md:grid-cols-[0.9fr_1.1fr]"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onFocusCapture={() => setIsPaused(true)}
          onBlurCapture={() => setIsPaused(false)}
        >
          <div className="relative flex min-h-[22rem] flex-col justify-between p-7 md:min-h-[28rem] md:p-10 lg:p-12">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeIndex}
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-6"
              >
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#ff5c7a]">
                  {activeUpdate.eyebrow}
                </p>
                <div className="space-y-4">
                  <h3 className="max-w-lg font-display text-4xl leading-[1.05] text-white md:text-5xl">
                    {activeUpdate.title}
                  </h3>
                  <p className="max-w-xl text-base leading-relaxed text-white/65 md:text-lg">
                    {activeUpdate.description}
                  </p>
                </div>
              </motion.div>
            </AnimatePresence>

            <div className="mt-10 flex items-center gap-3" aria-label="Choose a recent update">
              {recentUpdates.map((update, index) => (
                <button
                  key={update.title}
                  type="button"
                  onClick={() => setActiveIndex(index)}
                  aria-label={`Show update ${index + 1}: ${update.title}`}
                  aria-current={activeIndex === index ? "true" : undefined}
                  className="group flex h-8 items-center"
                >
                  <span
                    className={`block h-px transition-all duration-500 ${
                      activeIndex === index ? "w-12 bg-[#ff5c7a]" : "w-7 bg-white/25 group-hover:bg-white/55"
                    }`}
                  />
                  <span className="sr-only">{update.title}</span>
                </button>
              ))}
              <span className="ml-2 font-mono text-[11px] text-white/35">
                0{activeIndex + 1} / 0{recentUpdates.length}
              </span>
            </div>
          </div>

          <div className="relative min-h-[18rem] overflow-hidden border-t border-white/10 md:min-h-[28rem] md:border-l md:border-t-0">
            <AnimatePresence initial={false}>
              <motion.img
                key={activeUpdate.image}
                src={activeUpdate.image}
                alt={activeUpdate.alt}
                loading="lazy"
                decoding="async"
                initial={reduceMotion ? false : { opacity: 0, scale: 1.025 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 h-full w-full object-cover grayscale-[15%]"
                style={{ objectPosition: activeUpdate.imagePosition }}
              />
            </AnimatePresence>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/10" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_15%,rgba(255,255,255,0.08),transparent_35%)]" />
          </div>
        </div>
      </div>
    </section>
  );
}
